import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Page navigation for report data table.
 */
const ReportPaginator = ({ currentPage, totalPages, totalRows, pageSize, onPageChange }) => {
  if (totalPages <= 1) return null;

  const startRow = (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalRows);

  // Build page number buttons — show up to 7 pages with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const btnStyle = (active) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minWidth: '28px', height: '28px', padding: '0 6px',
    borderRadius: 'var(--radius-sm)', fontSize: '12px', fontWeight: active ? 700 : 400,
    border: active ? '1px solid var(--color-primary)' : 'var(--border-width) solid var(--color-border)',
    background: active ? 'var(--color-primary-light)' : 'var(--color-bg-white)',
    color: active ? 'var(--color-primary)' : 'var(--color-gray-700)',
    cursor: 'pointer'
  });

  const navBtnStyle = (disabled) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '28px', height: '28px',
    borderRadius: 'var(--radius-sm)', border: 'var(--border-width) solid var(--color-border)',
    background: disabled ? 'var(--color-gray-100)' : 'var(--color-bg-white)',
    color: disabled ? 'var(--color-gray-400)' : 'var(--color-gray-700)',
    cursor: disabled ? 'not-allowed' : 'pointer'
  });

  return (
    <div className="stui-row-between" style={{ padding: 'var(--spacing-sm) var(--spacing-md)', marginTop: 'var(--spacing-xs)' }}>
      <span className="stui-text-sm-muted">
        Showing {startRow.toLocaleString()}-{endRow.toLocaleString()} of {totalRows.toLocaleString()}
      </span>

      <div className="stui-row-4">
        {/* First page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={navBtnStyle(currentPage === 1)}
          title="First page"
        >
          <ChevronsLeft size={14} />
        </button>

        {/* Previous */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={navBtnStyle(currentPage === 1)}
          title="Previous page"
        >
          <ChevronLeft size={14} />
        </button>

        {/* Page numbers */}
        {getPageNumbers().map((page, i) => (
          page === '...' ? (
            <span key={`ellipsis-${i}`} className="stui-text-xs stui-c-muted" style={{ padding: '0 4px' }}>
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              style={btnStyle(page === currentPage)}
            >
              {page}
            </button>
          )
        ))}

        {/* Next */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={navBtnStyle(currentPage === totalPages)}
          title="Next page"
        >
          <ChevronRight size={14} />
        </button>

        {/* Last page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          style={navBtnStyle(currentPage === totalPages)}
          title="Last page"
        >
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default ReportPaginator;
